// Module 1 — Onglet Documents (livraison 2) : un seul espace, classé par catégorie.
// Consulter, déposer, remplacer une version (l'ancienne reste consultable), voir les échéances
// et ce qui manque. Chaque catégorie suit la confidentialité ; le dépôt est réservé au service RH.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { FolderOpen, FileText, Lock, Upload, RefreshCw, X, CheckCircle2, CircleAlert, Clock, ChevronDown } from 'lucide-react';
import LienFichierSecurise from '../LienFichierSecurise';
import { Carte, Badge, Bouton, BandeauInfo, Squelette, formaterDate } from '../../design/composants';

const ETAT_ATTENDUE = {
  presente: { ton: 'succes', libelle: 'Présente', Icone: CheckCircle2 },
  manquante: { ton: 'alerte', libelle: 'Manquante', Icone: CircleAlert },
  expiree: { ton: 'danger', libelle: 'Expirée', Icone: CircleAlert },
  bientot: { ton: 'alerte', libelle: 'Expire bientôt', Icone: Clock },
};
const lien = (p) => p.lien.chemin || `/api/documents/${p.lien.document_id}/fichier`;

function ModaleDepot({ salarieId, categorie, piece, onFermer, onTermine }) {
  const remplacement = !!piece;
  const [f, setF] = useState({ type_piece: piece?.type_piece || categorie.types[0]?.valeur || '', date_document: '', date_expiration: '', description: '' });
  const [fichier, setFichier] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const envoyer = async () => {
    if (!fichier) { setErreur('Choisissez un fichier (PDF, JPG ou PNG, 5 Mo au plus).'); return; }
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => { if (v) fd.append(k, v); });
    fd.append('fichier', fichier);
    setEnCours(true); setErreur('');
    try {
      const url = remplacement ? `/api/salaries/${salarieId}/documents/${piece.id}/remplacer` : `/api/salaries/${salarieId}/documents/${categorie.cle}`;
      const { data } = await axios.post(url, fd);
      onTermine(data.message);
    } catch (e) { setErreur(e.response?.data?.message || 'Le dépôt n\'a pas pu être réalisé.'); } finally { setEnCours(false); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label={remplacement ? 'Remplacer la pièce' : 'Déposer une pièce'} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="rh-carte" style={{ width: 500, maxWidth: '100%', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0 }}>
        <div className="rh-carte-entete" style={{ marginBottom: 6 }}>
          <h2 className="rh-carte-titre">{remplacement ? <RefreshCw size={17} aria-hidden="true" /> : <Upload size={17} aria-hidden="true" />}{remplacement ? 'Remplacer par une nouvelle version' : `Déposer — ${categorie.libelle}`}</h2>
          <button type="button" className="rh-btn rh-btn-discret rh-btn-icone" onClick={onFermer} aria-label="Fermer"><X size={18} /></button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--sg-texte-3)' }}>
          {remplacement ? `« ${piece.libelle} » restera consultable dans les versions précédentes.` : 'La pièce est rangée dans cette catégorie et protégée selon sa nature.'}
        </p>
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {!remplacement && (
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Type de pièce <span style={{ color: 'var(--sg-danger)' }}>*</span></span>
            <select className="rh-champ-saisie" value={f.type_piece} onChange={(e) => setF({ ...f, type_piece: e.target.value })}>
              {categorie.types.map((t) => <option key={t.valeur} value={t.valeur}>{t.libelle}</option>)}
            </select>
          </label>
        )}
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Fichier <span style={{ color: 'var(--sg-danger)' }}>*</span></span>
          <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="rh-champ-saisie" style={{ paddingTop: 7 }} onChange={(e) => setFichier(e.target.files?.[0] || null)} aria-label="Fichier à déposer" />
        </label>
        <div className="rh-grille">
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Date du document</span>
            <input type="date" className="rh-champ-saisie" value={f.date_document} onChange={(e) => setF({ ...f, date_document: e.target.value })} />
          </label>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Date d'expiration (si la pièce expire)</span>
            <input type="date" className="rh-champ-saisie" value={f.date_expiration} onChange={(e) => setF({ ...f, date_expiration: e.target.value })} aria-label="Date d'expiration" />
          </label>
        </div>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Libellé (facultatif)</span>
          <input className="rh-champ-saisie" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Ex. CNI renouvelée en 2026" />
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Bouton onClick={onFermer}>Annuler</Bouton>
          <Bouton variante="primaire" onClick={envoyer} disabled={enCours}>{enCours ? 'Dépôt en cours…' : remplacement ? 'Enregistrer la nouvelle version' : 'Déposer la pièce'}</Bouton>
        </div>
      </div>
    </div>
  );
}

export default function DocumentsSalarie({ employeId, peutGerer, onMessage }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const [depot, setDepot] = useState(null); // { categorie, piece? }
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let actif = true;
    axios.get(`/api/salaries/${employeId}/documents`)
      .then(({ data }) => { if (actif) setDonnees(data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.message || 'Les documents n\'ont pas pu être chargés.'); });
    return () => { actif = false; };
  }, [employeId, version]);

  if (erreur) return <BandeauInfo ton="danger">{erreur}</BandeauInfo>;
  if (!donnees) return <Carte><Squelette largeur="50%" /><Squelette largeur="35%" style={{ marginTop: 10 }} /></Carte>;

  const utiles = donnees.categories.filter((c) => c.pieces.length > 0 || c.attendues.length > 0);
  const vides = donnees.categories.filter((c) => c.pieces.length === 0 && c.attendues.length === 0);
  const termine = (m) => { setDepot(null); setVersion((v) => v + 1); if (onMessage) onMessage(m); };

  return (
    <div>
      {depot && <ModaleDepot salarieId={employeId} categorie={depot.categorie} piece={depot.piece} onFermer={() => setDepot(null)} onTermine={termine} />}

      {donnees.attendues.length > 0 && (
        <Carte titre="Pièces attendues" Icone={CheckCircle2}>
          <ul className="rh-liste">
            {donnees.attendues.map((a) => {
              const e = ETAT_ATTENDUE[a.etat];
              const cat = donnees.categories.find((c) => c.cle === a.categorie);
              return (
                <li key={a.type}>
                  <e.Icone size={16} color={`var(--sg-${e.ton === 'succes' ? 'succes' : e.ton === 'danger' ? 'danger' : 'alerte'})`} aria-hidden="true" />
                  <span style={{ flex: 1 }}>{a.libelle}</span>
                  <Badge ton={e.ton}>{e.libelle}</Badge>
                  {peutGerer && a.etat === 'manquante' && <Bouton petit Icone={Upload} onClick={() => setDepot({ categorie: cat })}>Déposer</Bouton>}
                </li>
              );
            })}
          </ul>
        </Carte>
      )}

      {utiles.map((c) => (
        <Carte key={c.cle} titre={c.libelle} Icone={FolderOpen}
          action={<span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {c.restreint && <Badge ton="neutre"><Lock size={11} aria-hidden="true" />Accès restreint</Badge>}
            {peutGerer && <Bouton petit Icone={Upload} onClick={() => setDepot({ categorie: c })}>Déposer</Bouton>}
          </span>}>
          {c.pieces.length === 0 ? <p style={{ margin: 0, fontSize: 13, color: 'var(--sg-texte-3)' }}>Aucune pièce déposée dans cette catégorie.</p> : (
            <ul className="rh-liste">
              {c.pieces.map((p) => (
                <li key={p.id} style={{ flexWrap: 'wrap' }}>
                  <FileText size={16} color="var(--sg-texte-3)" aria-hidden="true" />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <LienFichierSecurise chemin={lien(p)} style={{ color: 'var(--sg-primaire)', fontWeight: 500, textDecoration: 'none' }}>{p.libelle}</LienFichierSecurise>
                    <div className="rh-cellule-secondaire">
                      {formaterDate(p.date) || 'Date non renseignée'}
                      {p.date_expiration && ` · expire le ${formaterDate(p.date_expiration)}`}
                      {p.depose_par && ` · déposé par ${p.depose_par}`}
                      {p.rattachee && ' · rattachée à une saisie (famille, carrière, congé)'}
                    </div>
                    {p.versions.length > 0 && (
                      <details style={{ marginTop: 4 }}>
                        <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--sg-texte-3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><ChevronDown size={12} aria-hidden="true" />{p.versions.length} version{p.versions.length > 1 ? 's' : ''} précédente{p.versions.length > 1 ? 's' : ''}</summary>
                        <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0 }}>
                          {p.versions.map((v) => (
                            <li key={v.id} style={{ fontSize: 12, padding: '3px 0', color: 'var(--sg-texte-3)', border: 0 }}>
                              <LienFichierSecurise chemin={`/api/documents/${v.id}/fichier`} style={{ color: 'var(--sg-texte-2)' }}>{v.libelle || v.nom_fichier}</LienFichierSecurise>
                              {v.date_expiration && ` · expirait le ${formaterDate(v.date_expiration)}`}{v.remplace_le && ` · remplacée le ${formaterDate(v.remplace_le)}`}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                  {p.expiration === 'expiree' && <Badge ton="danger">Expirée</Badge>}
                  {p.expiration === 'bientot' && <Badge ton="alerte">Expire bientôt</Badge>}
                  {peutGerer && p.remplacable && <Bouton petit variante="discret" Icone={RefreshCw} onClick={() => setDepot({ categorie: c, piece: p })}>Remplacer</Bouton>}
                </li>
              ))}
            </ul>
          )}
        </Carte>
      ))}

      {vides.length > 0 && (
        <Carte>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--sg-texte-3)', flex: 1 }}>Sans document : {vides.map((c) => c.libelle).join(', ')}.</span>
            {peutGerer && (
              <select className="rh-champ-saisie" style={{ width: 'auto' }} value="" aria-label="Déposer dans une autre catégorie"
                onChange={(e) => { const c = vides.find((x) => x.cle === e.target.value); if (c) setDepot({ categorie: c }); }}>
                <option value="">Déposer dans une autre catégorie…</option>
                {vides.map((c) => <option key={c.cle} value={c.cle}>{c.libelle}</option>)}
              </select>
            )}
          </div>
        </Carte>
      )}
    </div>
  );
}
